import { bound } from "@frui.ts/helpers";
import { onlyResolvesLast } from "awesome-only-resolves-last-promise/dist";
import { action, computed, observable, when } from "mobx";
import ChargeRepository from "repositories/chargeRepository";
import ChargePriceListsResponse from "entities/chargePriceListsResponse";
import ChargePriceListResponse from "entities/chargePriceListResponse";
import ChargeTypeResponse from "entities/chargeTypeResponse";
import ChargeValidity from "entities/chargeValidity";
import EnumService from "./enumService";
import ChargePriceListStorageRepository from "../repositories/chargePriceListStorageRepository/chargePriceListStorageRepository";
import ChargeTypesStorageRepository from "repositories/chargeTypesStorageRepository/chargeTypesStorageRepository";
import FuelTypeResponse, { fuelTypeOrder } from "entities/fuelTypeResponse";
import FuelType from "entities/fuelType";
import ChargePriceListItemResponse from "entities/chargePriceListItemResponse";

export default class ChargeTypesService {
  @observable private _chargeTypes: ChargeTypeResponse[] = [];
  @observable private _fuelTypes: FuelTypeResponse[] = [];
  @observable private _chargePriceListItemsFlattened: ChargePriceListItemResponse[];
  @observable private _chargePriceList: ChargePriceListResponse;
  @observable isInitialized = false;
  @observable initializationInProgress = false;
  getUntilValidity = onlyResolvesLast(this.getValidity);

  constructor(
    private repository: ChargeRepository,
    private enumService: EnumService,
    private priceListStorage: ChargePriceListStorageRepository,
    private chargeTypesStorage: ChargeTypesStorageRepository
  ) {}

  @action.bound
  async initialize(includeInvalid: boolean) {
    if (this.initializationInProgress) {
      await when(() => !this.initializationInProgress);
      return;
    }

    this.initializationInProgress = true;
    const priceLists = await this.repository.chargePriceList(includeInvalid);
    if (priceLists instanceof ChargePriceListsResponse) {
      this._chargePriceListItemsFlattened = priceLists.items.flatMap(x => x.items.flatMap(y => y));

      // If includeInvalid is false, the BE always returns only one currently valid charge price list, so no need to filter it.
      this._chargePriceList = priceLists.items[0];
      if (this._chargePriceList) {
        this.priceListStorage.addPriceList(this._chargePriceList);
      }
    }
    this._chargeTypes = (await this.enumService.lazyValues("charge_types")) as ChargeTypeResponse[];
    this._fuelTypes = (await this.enumService.lazyValues("fuel_types")) as FuelTypeResponse[];
    this.chargeTypesStorage.addChargeTypes(this._chargeTypes);
    this.isInitialized = true;
    this.initializationInProgress = false;
  }

  @bound
  private getValidity(chargeTypeId: string, validSince: Date) {
    return this.repository.chargeValidity(chargeTypeId, validSince);
  }

  @bound
  async startInitialization(includeInvalid = false) {
    this.initialize(includeInvalid);
  }

  @computed
  get chargeTypes() {
    if (!this.isInitialized) {
      this.startInitialization();
      const chargeTypes = this.chargeTypesStorage.getChargeTypes();

      if (chargeTypes) {
        return chargeTypes;
      } else {
        return [
          this.generateMockChargeType(ChargeValidity.Annual),
          this.generateMockChargeType(ChargeValidity.Days10),
          this.generateMockChargeType(ChargeValidity.Days30),
          this.generateMockChargeType(ChargeValidity.SingleDay),
        ] as ChargeTypeResponse[];
      }
    }
    return this._chargeTypes;
  }

  @computed
  get chargePriceList() {
    return this._chargePriceList ?? this.priceListStorage.getPriceList();
  }

  @computed
  get validChargeTypes() {
    return this.chargeTypes.filter(type => !type.isDeleted);
  }

  generateMockChargeType(chargeValidity: ChargeValidity) {
    return {
      id: chargeValidity,
      isDeleted: false,
      validFor: chargeValidity,
    };
  }

  findChargeType(chargeTypeId?: string) {
    return this.chargeTypes.find(chargeType => chargeType.id === chargeTypeId);
  }

  hasPrices(item: ChargeTypeResponse) {
    const isMockChargeType = item.id == item.validFor;
    if (!this.chargePriceList?.includedChargeTypeIds || isMockChargeType) {
      return true;
    }
    return this.chargePriceList.includedChargeTypeIds.includes(item.id);
  }

  populatePrices(item: ChargeTypeResponse) {
    const prices = this.chargePriceList?.items?.filter(x => x.chargeTypeId === item.id);
    if (!prices) {
      return item;
    }

    const standardFuelType = this._fuelTypes.find(x => x.fuelType === FuelType.Other);
    const standardPrice = prices.find(x => x.fuelTypeId === standardFuelType?.id)?.price;
    item._standardPrice = standardPrice;

    const ecoPrices = prices.filter(x => x.fuelTypeId !== standardFuelType?.id).map(x => x.price);
    item._ecoPriceMin = Math.min(...ecoPrices);
    item._ecoPriceMax = Math.max(...ecoPrices);
    return item;
  }

  getPrice(priceListItemId: string, includeInvalid = false) {
    return this.getPriceItemList(includeInvalid)?.find(x => x.id === priceListItemId)?.price;
  }

  getPriceListItemFuelType(id: string, includeInvalid = false) {
    const chargePriceListItem = this.getPriceItemList(includeInvalid)?.find(x => x.id === id);
    if (!chargePriceListItem) {
      return undefined;
    }

    return this._fuelTypes.find(x => x.id === chargePriceListItem.fuelTypeId)?.fuelType;
  }

  getPriceListItemChargeType(id: string, includeInvalid = false) {
    const chargePriceListItem = this.getPriceItemList(includeInvalid)?.find(x => x.id === id);
    if (!chargePriceListItem) {
      return undefined;
    }

    return this._chargeTypes.find(x => x.id === chargePriceListItem.chargeTypeId);
  }

  getPriceItemList(includeInvalid: boolean) {
    return includeInvalid ? this._chargePriceListItemsFlattened : this.chargePriceList?.items;
  }

  setChargeTypes(chargeTypes: ChargeTypeResponse[]) {
    this._chargeTypes = chargeTypes;
  }

  getPricesWithFuelTypes(chargeType: ChargeTypeResponse) {
    const fuelPrices = this.getPriceItemList(false).filter(x => x.chargeTypeId === chargeType.id);
    const fuelTypePrices = this._fuelTypes
      .map(type => {
        const matchingFuelPrice = fuelPrices.find(x => x.fuelTypeId === type.id);
        if (!matchingFuelPrice) {
          return undefined;
        }

        return {
          id: type.id,
          fuelType: type.fuelType,
          price: matchingFuelPrice.price,
        };
      })
      .filter(x => x !== undefined)
      .map(x => x!)
      .sort((a, b) => {
        return fuelTypeOrder(a.fuelType) - fuelTypeOrder(b.fuelType);
      });

    //edaz-task #1167
    fuelTypePrices.push({
      id: Math.random().toString(),
      fuelType: FuelType.Electromobile,
      price: 0,
    });

    return {
      id: chargeType.id,
      description: chargeType.description,
      validFor: chargeType.validFor,
      prices: fuelTypePrices,
    };
  }

  getAvailableFuelTypesSorted() {
    const availableFuelTypeIds = this.getPriceItemList(false).map(x => x.fuelTypeId);
    return this._fuelTypes
      .filter(x => availableFuelTypeIds.includes(x.id))
      .sort((a, b) => {
        return fuelTypeOrder(a.fuelType) - fuelTypeOrder(b.fuelType);
      });
  }
}
